import { MINT } from "../../constants/images";
import Modal from "./Modal.component";
import { useContext, useEffect, useState } from "react";
import Button from "@components/common/Button.component";
import axios from "axios";
import ReactTooltip from "react-tooltip";
import { HiOutlineInformationCircle } from "react-icons/hi";
import toasterService from "@components/common/toaster/ToasterService.component";
import {
  getTransactionResult,
  objectToHexWithPrefix,
  SCOREMethodGetCall,
  SCOREMethodSetCall,
} from "@services/icon";
import { ENUM_SCORES } from "@enum/contracts.enum";
import { ICON_STATES } from "@enum/transaction.enum";
import { ledgerContext } from "@src/core/context/ledgerContext";
import { walletContext } from "@src/core/context/walletContext";
import { WALLET_TYPE } from "@enum/walletType.enum";

interface IGangstaBetInfo {
  name: string | null;
  imgUrl: string | null;
}

const GangstaBetPickerModal = ({
  setPopupOpen,
  setMintingSuccess,
  connectedAccount,
  setMintingLoading,
  isMintingLoading,
  isMintingSuccess,
}: any) => {
  if (process.env.REACT_APP_ENV === "production") {
    console.log = () => {};
    console.debug = () => {};
    console.warn = () => {};
  }
  const [nftId, setNftId] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [gangstabetInfo, setGanstabetInfo] = useState<IGangstaBetInfo>({
    name: null,
    imgUrl: null,
  });

  const ledgerContextValue = useContext(ledgerContext);
  const walletContextValue = useContext(walletContext);

  const getTx = async (tx: string) => {
    const res: any = await getTransactionResult(tx);
    if (res.status === ICON_STATES.PENDING) {
      getTxResultWithTimer(tx);
    } else if (res.status === ICON_STATES.SUCCESS) {
      setMintingLoading(false);
      setMintingSuccess(true);
    } else {
      toasterService.set({
        title: "ERROR",
        message: `Transaction failed during reservation.`,
        type: "error",
      });
      setMintingLoading(false);
    }
  };

  const getTxResultWithTimer = (txHash: string) => {
    setTimeout(() => {
      getTx(txHash);
    }, 5000);
  };

  const eventHandler: any = (event: any) => {
    if (event.detail.type === "RESPONSE_JSON-RPC") {
      const txHash = event.detail.payload.result;
      getTxResultWithTimer(txHash);
    } else if (event.detail.type === "CANCEL_JSON-RPC") {
      setMintingLoading(false);
    } else {
      setError("NFT reservation failed! ");
    }
  };

  useEffect(() => {
    window.addEventListener("ICONEX_RELAY_RESPONSE", eventHandler, false);
    return () => {
      window.removeEventListener("ICONEX_RELAY_RESPONSE", eventHandler);
    };
  }, []);

  const handleGetGangstaBetInfo = async () => {
    setLoading(true);
    const nftInfoUrl = "https://api.gangstabet.io/api/public/token/";
    const cloudFrontUrl = "https://d2l8dezwbqkb9.cloudfront.net/ipfs/img/";

    if (nftId > 0 && nftId <= 5555) {
      setError("");
      const gbInfo = await axios.get(
        nftInfoUrl + nftId
      );

      if (
        gbInfo?.data.hasOwnProperty("name") &&
        gbInfo?.data.hasOwnProperty("index")
      ) {
        setGanstabetInfo({
          imgUrl: cloudFrontUrl+gbInfo.data.index+".gif",
          name: gbInfo.data.name,
        });
        setLoading(false);
      }
    } else {
      if (nftId !== 0) {
        setError("Invalid NFT Id, valid NFT id range from 1 to 5555!");
      }
      setGanstabetInfo({
        imgUrl: null,
        name: null,
      });
      setLoading(false);
    }
  };

  const handleVerification = async () => {
    const isOwnerOf = await SCOREMethodGetCall(
      "ownerOf",
      ENUM_SCORES.GANGSTABET_SCORE,
      {
        _tokenId: "0x" + nftId.toString(16),
      }
    );

    const checkReservedOrNot = await SCOREMethodGetCall(
      "getReservation",
      ENUM_SCORES.ILLUSTRATION_MINT,
      {
        id: "0x" + nftId.toString(16),
      }
    );

    let verification = isOwnerOf === connectedAccount;
    if (verification) {
      if (checkReservedOrNot === undefined || checkReservedOrNot === null) {
        setError(null);
        setReservation();
      } else {
        setMintingLoading(false);
        setError("NFT is already reserved!");
      }
    } else {
      setMintingLoading(false);
      setError("You don't own the selected NFT!");
    }
  };

  const setReservation = async () => {
    setMintingLoading(true);

    const getReservationCost = await SCOREMethodGetCall(
      "getReservationCost",
      ENUM_SCORES.ILLUSTRATION_MINT
    );

    const isLedger = walletContextValue.type === WALLET_TYPE.LEDGER;

    const txHash = await SCOREMethodSetCall(
      connectedAccount,
      "transfer",
      ENUM_SCORES.CROWN,
      {
        _to: ENUM_SCORES.TOKEN_DISTRIBUTION,
        _value: getReservationCost,
        _data: objectToHexWithPrefix({
          nftId: nftId.toString(),
          amount: Number(getReservationCost)
            .toLocaleString()
            .replaceAll(",", ""),
          destinationContract: ENUM_SCORES.ILLUSTRATION_MINT,
          deposit: false,
        }),
      },
      isLedger,
      ledgerContextValue.ledgerInfo.ledgerPath,
      ledgerContextValue.ledgerInfo.ledgerTransport
    );

    if (isLedger) {
      if (txHash?.name === "TransportStatusError") {
        alert("Cancelled");
      } else if (txHash) {
        getTxResultWithTimer(txHash as string);
      } else {
        console.error("Error on ledger");
      }
    }
  };

  useEffect(() => {
    const getData: any = window.setTimeout(() => {
      handleGetGangstaBetInfo();
    }, 500);

    return () => clearTimeout(getData);
  }, [nftId]);

  const handleKeystrokeChange = (event: any) => {
    setNftId(Number(event.target.value));
  };

  return (
    <div>
      <Modal
        setPopupOpen={setPopupOpen}
        title="Select your Gangstabet"
        style={{
          width: "743px",
          display: `${isMintingLoading || isMintingSuccess ? "none" : "block"}`,
        }}
      >
        <ReactTooltip backgroundColor="black" multiline={true} />
        <div className="nft-detail-container">
          <div className="nft-detail-subtitle">
            You will need to input the Gangstabet NFT's ID.
          </div>
          <div className="nft-detail-wrapper">
            {gangstabetInfo.imgUrl && gangstabetInfo.name && !loading ? (
              <div className="nft__details">
                <img src={gangstabetInfo.imgUrl} width="140" /> <br />
                {/* <p>{gangstabetInfo.name}</p> */}
              </div>
            ) : (
              <div className="nft__details">
                <img src={MINT.NFTHOLDER} width="140" />
                <p> </p>
              </div>
            )}

            <div className="nft-detail-input">
              {gangstabetInfo.name && !loading ? (
                <p>{gangstabetInfo.name}</p>
              ) : (
                <p className="gangstabet-id-info">
                  GangstaBet ID{" "}
                  <span data-tip="If you are unaware of your GangstaBet Id, visit your GangstaBet profile.You will see your GangstaBet Id in the URL.<br/>Example: https://gangstabet.io/profile/4169<br/> Here, 4169 is the gangstabet id.">
                    <HiOutlineInformationCircle />
                  </span>
                </p>
              )}
              <input
                type="number"
                className="nft-detail-inputfield"
                placeholder="1234"
                min="0"
                onChange={handleKeystrokeChange}
              />
              <Button
                fullWidth
                onClick={handleVerification}
                disabled={loading || nftId === 0}
              >
                {loading ? <p>Loading</p> : <p>Reserve</p>}
              </Button>
            </div>
          </div>
          {error ? (
            <p style={{ background: "red" }}>&nbsp; ERROR: {error} &nbsp;</p>
          ) : (
            <p> Note: You can only reserve the NFTs that you own!</p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default GangstaBetPickerModal;
